import type { HeaderOpenModalProps } from '../../core/layouts/MasterLayout';

import { getCookie } from 'cookies-next';
import { useRouter } from 'next/compat/router';
import { useCallback, useEffect, useState } from 'react';

import { useAppContext } from '~/utilities/context/static/AppContext';
import { storageAvailable } from '~/utilities/helpers';
import { isMatchLocaleCountry, isValidLocale } from '~/utilities/ssr';

type Suggestion = { language: string; country: string };

/**
 * The auto-open decision per locale for the current page view: ownership moves between the header
 * and the mobile drawer when the viewport crosses `lg` (rotation, resize), and the new owner must
 * not prompt again. Cleared on navigation, so each page view decides again, as before the drawer:
 * the prompt comes back on the next page until the visitor closes it (`lst`). `null`: checked,
 * nothing to suggest.
 */
const decisions = new Map<string, Suggestion | null>();

/** Forgets the auto-open decisions: on navigation, and in tests. */
export const resetLocaleSelectorAutoOpen = (): void => decisions.clear();

// Shallow routing (e.g. PLP filters in the URL) keeps the page mounted: same page view.
const resetOnNavigation = (_url: string, { shallow }: { shallow?: boolean } = {}): void => {
  if (!shallow) {
    resetLocaleSelectorAutoOpen();
  }
};

const getIpSuggestion = (locale: string): Suggestion | null => {
  const isLocalStorageAvailable = storageAvailable('localStorage');
  const ipLocale = getCookie('ipLocale')?.toString() || 'default';
  const lst = isLocalStorageAvailable ? localStorage.getItem('lst') : '';
  const lstTO = (isLocalStorageAvailable && localStorage.getItem('lst_TO')) || '0';
  const lstTOTimestamp = Number.parseInt(lstTO, 10);

  if (
    (lst !== 'true' || lstTOTimestamp * 1000 < Date.now()) &&
    isValidLocale(ipLocale) &&
    !isMatchLocaleCountry(ipLocale, locale)
  ) {
    const [language, country] = ipLocale.split('_');

    return { language, country };
  }

  return null;
};

type LocaleSelectorAutoOpen = {
  /** Country / language suggested by the visitor's IP (`ipLocale` cookie), preselected in the modal. */
  suggestedCountry?: string;
  suggestedLanguage?: string;
  /** For a manual open: the modal starts on the current language again. */
  clearSuggestedLanguage: () => void;
};

/**
 * Opens the locale selector automatically when the visitor's IP locale (`ipLocale` cookie) does not
 * match the current locale, unless they chose a locale recently (`lst` / `lst_TO`), and keeps the
 * IP country / language as the modal's preselection. It opens at most once per page view and
 * locale; a later owner only takes over the suggestion.
 *
 * Shared by the header locale selector button and the mobile TLN drawer: exactly one of them must
 * pass `enabled`, the one rendering the modal for the current viewport.
 */
export const useLocaleSelectorAutoOpen = (
  setOpenedModal: HeaderOpenModalProps['setOpenedModal'],
  enabled = true
): LocaleSelectorAutoOpen => {
  const { locale } = useAppContext();
  const router = useRouter();
  const [suggestedLanguage, setSuggestedLanguage] = useState<string>();
  const [suggestedCountry, setSuggestedCountry] = useState<string>();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const isFirstDecision = !decisions.has(locale);

    if (isFirstDecision) {
      decisions.set(locale, getIpSuggestion(locale));
    }

    const suggestion = decisions.get(locale);

    if (!suggestion) {
      return;
    }

    setSuggestedLanguage(suggestion.language);
    setSuggestedCountry(suggestion.country);

    if (isFirstDecision) {
      setOpenedModal('locale-selector');
    }
  }, [enabled, locale, setOpenedModal]);

  // Only the owner (`enabled`) makes decisions, so only it clears them: one listener, not one per
  // mounted button.
  useEffect(() => {
    if (!router || !enabled) {
      return undefined;
    }

    router.events.on('routeChangeStart', resetOnNavigation);

    return () => {
      router.events.off('routeChangeStart', resetOnNavigation);
    };
  }, [enabled, router]);

  const clearSuggestedLanguage = useCallback(() => setSuggestedLanguage(''), []);

  return { suggestedCountry, suggestedLanguage, clearSuggestedLanguage };
};
